// src/pages/PostOpCount/PostOpCount.jsx
import React, { useEffect, useRef, useState } from "react";
import {
  FiSearch,
  FiUser,
  FiAlertTriangle,
  FiCalendar,
  FiCheckCircle,
  FiXCircle,
} from "react-icons/fi";
import { Tooltip } from "@material-ui/core";
import { toast } from "react-toastify";
import { motion } from "framer-motion";
import request from "../../utils/request";
import PageContainer from "../../components/PageContainer/PageContainer";
import df from "../../utils/dateFormater";
import styles from "./PostOpCount.module.css";
import { GiMedicalDrip } from "react-icons/gi";
import t from "../../utils/translation";

const CATS = [
  { key: "ok", label: t("BlankEntry223"), color: "green", icon: FiCheckCircle },
  {
    key: "implanted",
    label: t("BlankEntry222"),
    color: "blue",
    icon: GiMedicalDrip,
  },
  // { key: "broken", label: "Damaged", color: "red", icon: FiXCircle },
  {
    key: "lost",
    label: t("BlankEntry200"),
    color: "orange",
    icon: FiAlertTriangle,
  },
];

const PKG_STATUS = {
  ACCEPTED: 8,
  REJECTED: 9,
  MISSING: 11,
};

export default function PostOpCount() {
  const [dispatchList, setDispatchList] = useState([]);
  const [dispatchNumber, setDispatchNumber] = useState("");
  const [filteredSug, setFilteredSug] = useState([]);
  const [showDD, setShowDD] = useState(false);
  const ddRef = useRef();

  const [dispatch, setDispatch] = useState(null);
  const [loading, setLoading] = useState(false);

  const [countResponsible, setCountResponsible] = useState("");
  const [observations, setObservations] = useState("");

  const [plan, setPlan] = useState({});
  const saveRow = (k, r) => setPlan((p) => ({ ...p, [k]: r }));

  const fetchDispatchData = async () => {
    try {
      const { data } = await request().get(
        "/api/parameter/dispatch?dispatchStatus=2"
      );
      const nums = Array.isArray(data)
        ? data.filter((n) => typeof n === "number")
        : [];
      setDispatchList(nums);
      setFilteredSug(nums);
    } catch (error) {
      console.error("Error fetching dispatch data", error);
    }
  };

  useEffect(() => {
    fetchDispatchData();
  }, []);

  useEffect(() => {
    const h = (e) =>
      ddRef.current && !ddRef.current.contains(e.target) && setShowDD(false);
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const onInput = (v) => {
    setDispatchNumber(v);
    setFilteredSug(
      v ? dispatchList.filter((n) => n.toString().includes(v)) : dispatchList
    );
    setShowDD(true);
  };
  const onSelect = (n) => {
    setDispatchNumber(n.toString());
    setShowDD(false);
    fetchDispatch(n);
  };

  const fetchDispatch = async (n) => {
    const num = Number(n);
    if (!dispatchList.includes(num)) {
      toast.info(t("BlankEntry224"));
      return;
    }
    try {
      setLoading(true);
      setDispatch(null);
      setPlan({});
      const { data } = await request().get(
        `/api/dispatch/list?dispatchNumber=${num}`
      );
      const d = data.dispatches?.[0] || data[0];
      if (!d) {
        toast.error(t("BlankEntry116"));
        return;
      }
      d.packages = d.packages.map((p) => ({
        ...p,
        packageStatus: p.packageStatus ?? null,
        initialStatus: p.packageStatus ?? null,
      }));
      const init = {};
      d.packages.forEach((p) =>
        p.materials.forEach((m) => {
          const max = m.quantity ?? 1;
          init[`${p.packageId}-${m.materialId}`] = {
            ok: max,
            implanted: 0,
            broken: 0,
            lost: 0,
          };
        })
      );
      setPlan(init);
      setDispatch(d);
    } finally {
      setLoading(false);
    }
  };

  const moveAll = (pkg, dest) =>
    pkg.materials.forEach((m) => {
      const max = m.quantity ?? 1;
      saveRow(`${pkg.packageId}-${m.materialId}`, {
        ok: 0,
        implanted: 0,
        broken: 0,
        lost: 0,
        [dest]: max,
      });
    });

  const setVal = (k, dest, v, max) => {
    const n = Math.max(0, Number(v));
    const r = { ...plan[k], [dest]: n };
    if (Object.values(r).reduce((s, x) => s + Number(x), 0) > max) return;
    saveRow(k, r);
  };
  const confirm = async () => {
    if (!dispatch) {
      return;
    }

    const map = {
      ok: 9999,
      implanted: 12,
      broken: 13,
      lost: 14,
    };

    const incomplete = dispatch.packages.some((pkg) =>
      pkg.materials.some((m) => {
        const key = `${pkg.packageId}-${m.materialId}`;
        const row = plan[key];
        const total = m.quantity ?? 1;
        const sum = row
          ? Object.values(row).reduce((a, b) => a + Number(b), 0)
          : 0;
        return sum !== total;
      })
    );

    if (incomplete) {
      toast.info(
        t("BlankEntry233") || "All materials must be fully distributed."
      );
      return;
    }

    const packagesPayload = dispatch.packages
      .filter(
        (pkg) =>
          pkg.initialStatus !== PKG_STATUS.REJECTED &&
          pkg.initialStatus !== PKG_STATUS.MISSING
      )
      .map((pkg) => {
        const materialMap = {};

        pkg.materials.forEach((m) => {
          const key = `${pkg.packageId}-${m.materialId}`;
          const row = plan[key];
          if (!row) return;

          const statuses = Object.entries(row)
            .filter(([_, q]) => Number(q) > 0)
            .map(([k, q]) => ({
              quantity: Number(q),
              status: map[k],
            }));

          if (statuses.length > 0) {
            materialMap[m.materialId] = {
              materialId: m.materialId,
              MaterialStatuses: statuses,
            };
          }
        });

        const materialsArray = Object.values(materialMap);
        if (!materialsArray.length) return null;

        return {
          packageId: pkg.packageId,
          packageStatus: pkg.packageStatus,
          Materials: materialsArray,
        };
      })
      .filter(Boolean);

    const payload = {
      shippingResponsible: countResponsible.trim(),
      dispatchNumber: dispatch.dispatchNumber,
      observations,
      packages: packagesPayload,
    };

    try {
      await request().put("/api/dispatch/verify", payload);
      setDispatch(null);
      setDispatchNumber("");
      fetchDispatchData();
      toast.success(t("BlankEntry225"));
    } catch {
      toast.error("Error submitting count.");
    }
  };

  const handleInputChange = (e, { key, catKey, max, isSerial, material }) => {
    const raw = e.target.value;

    if (raw === "") {
      saveRow(key, { ...plan[key], [catKey]: "" });
      return;
    }

    const parsed = parseInt(raw);
    if (isNaN(parsed) || parsed < 0) return;

    const totalQty = material.quantity ?? 1;

    const behavesLikeSerial = isSerial || totalQty === 1;

    if (behavesLikeSerial) {
      if (parsed > 1) return;
      if (parsed === 1) {
        // Exclusivo
        const updated = Object.fromEntries(
          CATS.map((c) => [c.key, c.key === catKey ? 1 : 0])
        );
        saveRow(key, updated);
      } else {
        saveRow(key, { ...plan[key], [catKey]: 0 });
      }
      return;
    }

    // Non-serial, quantity > 1
    const currentRow = { ...plan[key], [catKey]: parsed };

    const totalAssigned = Object.entries(currentRow).reduce(
      (sum, [k, v]) => sum + (Number(v) || 0),
      0
    );

    if (totalAssigned > totalQty) {
      toast.info(t("BlankEntry244"));
      // Set this field to the max allowed (available remaining)
      const remaining =
        totalQty -
        Object.entries(plan[key])
          .filter(([k]) => k !== catKey)
          .reduce((sum, [_, v]) => sum + Number(v || 0), 0);

      saveRow(key, { ...plan[key], [catKey]: Math.max(0, remaining) });
      return;
    }

    saveRow(key, currentRow);
  };

  const handleBlur = ({ key, catKey }) => {
    const row = plan[key];
    if (row?.[catKey] === "") {
      saveRow(key, { ...row, [catKey]: 0 });
    }
  };

  return (
    <PageContainer
      title={t("BlankEntry218")}
      category={t("BlankEntry217")}
      categoryId={5}
      backUrl="/appreceptionmenu"
    >
      <div className={styles.wrapper}>
        <div className={styles.searchBox} ref={ddRef}>
          <FiSearch />
          <input
            placeholder={t("BlankEntry201")}
            value={dispatchNumber}
            onChange={(e) => onInput(e.target.value)}
            onFocus={() => setShowDD(true)}
            onKeyDown={(e) => e.key === "Enter" && onSelect(dispatchNumber)}
          />
          <button onClick={() => onSelect(dispatchNumber)}>
            {t("BlankEntry117")}
          </button>
          {showDD && (
            <ul className={styles.dropdown}>
              {filteredSug.length ? (
                filteredSug.map((n) => (
                  <li key={n} onClick={() => onSelect(n)}>
                    {n}
                  </li>
                ))
              ) : (
                <li className={styles.noItem}>{t("BlankEntry211")}</li>
              )}
            </ul>
          )}
        </div>

        {loading && (
          <div className="spinnerContainer">
            <div className={styles.spinner} /> {t("BlankEntry115")}…
          </div>
        )}

        {dispatch && (
          <motion.div
            className={styles.dispatchCard}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h2 className={styles.dispatchTitle}>
              {t("BlankEntry166")} {dispatch.dispatchNumber}
            </h2>

            <div className={styles.infoGrid}>
              <span>
                <FiUser /> {t("BlankEntry206")}: {dispatch.senderUser}
              </span>
              <span>
                <FiAlertTriangle /> {t("BlankEntry173")}:{" "}
                {dispatch.shippingResponsible}
              </span>
              <span>
                <FiSearch /> {t("BlankEntry207")}: {dispatch.areaName}
              </span>
              <span>
                <FiCalendar /> {df(dispatch.creationDate, "dateTime")}
              </span>
            </div>

            <div className={styles.dispatchExtras}>
              {/* <div className={styles.extraItem}>
                <label>{t("BlankEntry226")}:</label>
                <input
                  value={countResponsible}
                  onChange={(e) => setCountResponsible(e.target.value)}
                />
              </div> */}
              <div className={styles.extraItem}>
                <label>{t("ObservationSpan")}</label>
                <textarea
                  rows={2}
                  value={observations}
                  onChange={(e) => setObservations(e.target.value)}
                />
              </div>
            </div>

            {dispatch.packages.map((pkg) => {
              const locked =
                pkg.initialStatus === PKG_STATUS.REJECTED ||
                pkg.initialStatus === PKG_STATUS.MISSING;
              const acceptDisabled = locked;
              const rejectDisabled = locked ? true : false;
              const missingDisabled =
                locked || pkg.initialStatus === PKG_STATUS.ACCEPTED;
              return (
                <motion.div
                  key={pkg.packageId}
                  className={`${styles.pkgCard} ${locked ? styles.locked : ""}`}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <header className={styles.pkgHeader}>
                    <h3>
                      {pkg.packageNumber} — {pkg.packageName}
                      <span className={styles.countBadge}>
                        {pkg.materials.reduce(
                          (s, m) => s + (m.quantity ?? 1),
                          0
                        )}{" "}
                        {t("MaterialsSpan")}
                      </span>
                    </h3>
                    <div className={styles.pkgBtns}>
                      <Tooltip title="Accept">
                        <button
                          disabled={acceptDisabled}
                          className={`${styles.pkgBtn} ${
                            pkg.packageStatus === PKG_STATUS.ACCEPTED
                              ? styles.green
                              : ""
                          }`}
                          onClick={() =>
                            setDispatch((d) => ({
                              ...d,
                              packages: d.packages.map((p) =>
                                p.packageId === pkg.packageId
                                  ? { ...p, packageStatus: PKG_STATUS.ACCEPTED }
                                  : p
                              ),
                            }))
                          }
                        >
                          <FiCheckCircle className={styles.pkgIcon} />
                          <span>{t("BlankEntry214")}</span>
                        </button>
                      </Tooltip>
                      <Tooltip title="Reject">
                        <button
                          disabled={rejectDisabled}
                          className={`${styles.pkgBtn} ${
                            pkg.packageStatus === PKG_STATUS.REJECTED
                              ? styles.red
                              : ""
                          }`}
                          onClick={() =>
                            setDispatch((d) => ({
                              ...d,
                              packages: d.packages.map((p) =>
                                p.packageId === pkg.packageId
                                  ? { ...p, packageStatus: PKG_STATUS.REJECTED }
                                  : p
                              ),
                            }))
                          }
                        >
                          <FiXCircle className={styles.pkgIcon} />
                          <span>{t("BlankEntry144")}</span>
                        </button>
                      </Tooltip>
                      <Tooltip title="Missing">
                        <button
                          disabled={missingDisabled}
                          className={`${styles.pkgBtn} ${
                            pkg.packageStatus === PKG_STATUS.MISSING
                              ? styles.orange
                              : ""
                          }`}
                          onClick={() =>
                            setDispatch((d) => ({
                              ...d,
                              packages: d.packages.map((p) =>
                                p.packageId === pkg.packageId
                                  ? { ...p, packageStatus: PKG_STATUS.MISSING }
                                  : p
                              ),
                            }))
                          }
                        >
                          <FiAlertTriangle className={styles.pkgIcon} />
                          <span>{t("BlankEntry200")}</span>
                        </button>
                      </Tooltip>
                    </div>
                  </header>

                  <table className={styles.matTable}>
                    <thead>
                      <tr>
                        <th>{t("MaterialSpan")}</th>
                        <th>{t("SerialSpan")}</th>
                        <th className={styles.qtyCol}>{t("QuantitySpan")}</th>
                        {CATS.map((c) => (
                          <th key={c.key} className={styles.catHead}>
                            <button
                              disabled={locked}
                              className={`${styles.catBtn} ${styles[c.color]}`}
                              onClick={() => moveAll(pkg, c.key)}
                            >
                              <c.icon className={styles.catIcon} />
                              {c.label}
                            </button>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {pkg.materials.map((m) => {
                        const key = `${pkg.packageId}-${m.materialId}`;
                        const max = m.quantity ?? 1;
                        const row = plan[key];
                        return (
                          <tr key={key}>
                            <td>{m.materialName}</td>
                            <td>{m.serial || "—"}</td>
                            <td>{max}</td>
                            {CATS.map((c) => (
                              <td key={c.key}>
                                <input
                                  type="text"
                                  disabled={locked}
                                  value={
                                    row[c.key] === 0 ? "0" : row[c.key] ?? "0"
                                  }
                                  onChange={(e) =>
                                    handleInputChange(e, {
                                      key,
                                      catKey: c.key,
                                      max,
                                      isSerial: !!m.serial,
                                      material: m,
                                    })
                                  }
                                  onBlur={() =>
                                    handleBlur({ key, catKey: c.key })
                                  }
                                  className={styles.numInput}
                                />
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </motion.div>
              );
            })}

            <div className={styles.footer}>
              <button onClick={confirm} className={styles.confirm}>
                {t("ConfirmButton")}
              </button>
            </div>
          </motion.div>
        )}
      </div>
    </PageContainer>
  );
}
